/* Karaoke Studio — pitch scoring.
 *
 * Scores a singer against a reference melody:
 *   1. buildReference(vocalBuffer) runs the shared PitchTracker (YIN) over the
 *      separated vocal stem → a time→frequency lookup of the target melody.
 *   2. During playback the live mic signal is pitch-detected (autocorrelation
 *      on an AnalyserNode buffer — main-thread, no AudioWorklet needed) and
 *      fed to a Scorer that compares it to the reference at the same song time.
 *   3. Scoring is octave-folded (singing an octave off still counts), rests in
 *      the reference are ignored, and a per-difficulty cents tolerance maps
 *      deviation → a 0..1 frame score; the session score is their average.
 *
 * The pure functions (detectPitch / centsBetween / frameScore / createScorer)
 * carry no browser dependency and are unit-tested directly.
 */
(function () {
    'use strict';

    // Cents tolerance per difficulty — deviation beyond this scores 0.
    var TOLERANCE = { easy: 200, normal: 110, hard: 55 };

    // ── Autocorrelation pitch detection ─────────────────────────────────────
    // Returns the fundamental frequency in Hz, or 0 when the input is too
    // quiet or too noisy to carry a confident pitch.
    function detectPitch(buf, sampleRate) {
        var SIZE = buf.length;
        if (!SIZE) return 0;

        var rms = 0;
        for (var i = 0; i < SIZE; i++) rms += buf[i] * buf[i];
        rms = Math.sqrt(rms / SIZE);
        if (rms < 0.01) return 0;            // silence gate

        var minLag = Math.floor(sampleRate / 1000); // up to 1000 Hz
        var maxLag = Math.floor(sampleRate / 70);    // down to 70 Hz
        if (maxLag >= SIZE) maxLag = SIZE - 1;

        var energy0 = 0;
        for (var k = 0; k < SIZE; k++) energy0 += buf[k] * buf[k];

        var bestLag = -1, bestCorr = 0;
        for (var lag = minLag; lag <= maxLag; lag++) {
            var corr = 0;
            for (var j = 0; j < SIZE - lag; j++) corr += buf[j] * buf[j + lag];
            // Normalise so the clarity threshold is amplitude-independent.
            var norm = corr / (energy0 + 1e-12);
            if (norm > bestCorr) { bestCorr = norm; bestLag = lag; }
        }
        if (bestLag <= 0 || bestCorr < 0.2) return 0;   // no clear period

        // Parabolic interpolation around the peak for sub-sample accuracy.
        var f = sampleRate / bestLag;
        if (f < 70 || f > 1100) return 0;
        return f;
    }

    // ── Octave-folded cents distance ────────────────────────────────────────
    // Singing the right note class an octave away still counts as correct.
    function centsBetween(f1, f2) {
        if (f1 <= 0 || f2 <= 0) return Infinity;
        var cents = 1200 * Math.log2(f1 / f2);
        // fold into (-600, 600]
        cents = ((cents % 1200) + 1800) % 1200 - 600;
        return Math.abs(cents);
    }

    // 0..1 score for one frame. Silence when a note is expected scores 0.
    function frameScore(sungHz, refHz, tolCents) {
        if (refHz <= 0) return null;          // no note expected — caller skips
        if (sungHz <= 0) return 0;            // expected a note, got silence
        var dev = centsBetween(sungHz, refHz);
        if (dev >= tolCents) return 0;
        return 1 - dev / tolCents;            // linear falloff
    }

    // ── Session scorer ──────────────────────────────────────────────────────
    function createScorer(reference, opts) {
        opts = opts || {};
        var tol = TOLERANCE[opts.difficulty] || TOLERANCE.normal;
        var sum = 0, scored = 0, hits = 0;

        return {
            // timeS: song position, sungHz: live mic pitch (0 = silence).
            add: function (timeS, sungHz) {
                var refHz = reference ? reference.at(timeS) : 0;
                var s = frameScore(sungHz, refHz, tol);
                if (s === null) return;       // rest in the reference — ignore
                scored++;
                sum += s;
                if (s >= 0.5) hits++;
            },
            reset: function () { sum = 0; scored = 0; hits = 0; },
            result: function () {
                return {
                    score: scored ? Math.round((sum / scored) * 100) : 0,
                    scoredFrames: scored,
                    hitFrames: hits,
                    hitRate: scored ? hits / scored : 0,
                };
            },
        };
    }

    // ── Reference melody from the vocal stem ────────────────────────────────
    async function buildReference(audioBuffer, onProgress) {
        if (!window.PitchTracker || !audioBuffer) return null;
        var tracked = await window.PitchTracker.track(audioBuffer, {
            hopSec: 0.05,        // 50 ms is plenty for a sing-along target
            minConf: 0.8,
        });
        var frames = tracked.frames || [];
        if (!frames.length) return null;
        var hop = tracked.hopSec || 0.05;
        if (onProgress) onProgress(1);
        return {
            frames: frames,
            hopSec: hop,
            // Nearest-frame lookup; 0 when the target melody is silent there.
            at: function (timeS) {
                if (timeS < 0) return 0;
                var idx = Math.round(timeS / hop);
                if (idx < 0 || idx >= frames.length) return 0;
                var fr = frames[idx];
                return (fr && fr.freqHz > 0) ? fr.freqHz : 0;
            },
        };
    }

    // Reference melody from explicit note events (MIDI / UltraStar import) —
    // works even for backing-track uploads that never had a vocal stem.
    function referenceFromNotes(notes) {
        if (!notes || !notes.length) return null;
        var sorted = notes.slice().sort(function (a, b) { return a.startS - b.startS; });
        return {
            notes: sorted,
            at: function (timeS) {
                for (var i = 0; i < sorted.length; i++) {
                    if (sorted[i].startS > timeS) break;
                    if (timeS < sorted[i].endS) return sorted[i].freqHz || 0;
                }
                return 0;
            },
        };
    }

    window.KaraokeScoring = {
        TOLERANCE: TOLERANCE,
        detectPitch: detectPitch,
        centsBetween: centsBetween,
        frameScore: frameScore,
        createScorer: createScorer,
        buildReference: buildReference,
        referenceFromNotes: referenceFromNotes,
    };
})();
